import React, { useMemo } from 'react';
import styled from 'styled-components';
import { withTheme } from '@buoyhealth/common.buoy-theme';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import { Card } from '@buoyhealth/common.buoy-card';
import { BetaChip } from '@buoyhealth/common.buoy-chip';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import { RestartIcon } from '@buoyhealth/common.buoy-icons';
import ProgressBar from '../ProgressBar';
import useLoading from '../../hooks/useLoading';
import useInitializing from '../../hooks/useInitializing';
import useUncaughtErrors from '../../hooks/useUncaughtErrors';
import ErrorContainer from '../ErrorContainer';
import LoadingContainer from '../LoadingContainer';
import DisclaimerModal from '../DisclaimerModal';
import { IEmbeddedLayoutProps } from './types';

export const Paragraph = styled.p`
  ${({ theme }) => theme.typography.variants.primary.small};
  font-size: 18px;
  margin: 0;

  a {
    font-size: 18px;
  }
`;

const ChipContainer = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    flex-grow: 1;
  }
`;

const RestartButton = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    order: 2;
    margin-left: 8px;
  }
`;

const Layout = ({ children, showRestartButton, triggerRestart }: IEmbeddedLayoutProps) => {
  const theme = useTheme();
  const errors = useUncaughtErrors();
  const isLoading = useLoading();
  const isInitializing = useInitializing();
  const showLoader = isLoading || isInitializing;

  const Content = useMemo(() => {
    if (errors.length) {
      return <ErrorContainer triggerRestart={triggerRestart} />;
    }
    if (showLoader) {
      return <LoadingContainer />;
    }
    return children;
  }, [children, errors.length, showLoader, triggerRestart]);

  return (
    <Card backgroundColor={theme.palette.teal[0]} className="BuoyInterviews__Layout">
      <Grid container direction="column" spacing={2.5}>
        <Grid
          item
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <ChipContainer>
            <BetaChip ModalContent={<DisclaimerModal />} label="⚡️ Powered by AI" />
          </ChipContainer>
          <RestartButton>
            <Grow in={showRestartButton}>
              <IconButton onClick={triggerRestart}>
                <RestartIcon size={14} />
              </IconButton>
            </Grow>
          </RestartButton>
          <ProgressBar />
        </Grid>
        {Content}
      </Grid>
    </Card>
  );
};

export default withTheme(Layout);
